import React from 'react';
import PropTypes from 'prop-types';

import './OperationalCalendar.css';

const OperationalCalendar = ({
  title,
  view,
  viewOptions,
  onViewChange,
  onPrev,
  onNext,
  onToday,
  weekdays,
  days,
  selectedDayKey,
  onDaySelect,
  onDayContextMenu,
  filters,
  onFilterToggle,
  onFilterReset,
  showReset,
  visibleEventCount,
  onToggleFullscreen,
  isFullscreen,
}) => {
  const handleViewChange = (option) => {
    if (option.disabled || option.value === view) return;
    onViewChange(option.value);
  };

  return (
    <div className="operational-calendar">
      <div className="operational-calendar__header">
        <div>
          <p className="operational-calendar__eyebrow">Disponibilidad operativa</p>
          <h2>{title}</h2>
        </div>
        <div className="operational-calendar__controls">
          <div className="operational-calendar__view-toggle" role="tablist" aria-label="Cambiar vista">
            {viewOptions.map((option) => (
              <button
                type="button"
                key={option.value}
                className={`operational-calendar__view-option${view === option.value ? ' is-active' : ''}${option.disabled ? ' is-disabled' : ''}`}
                onClick={() => handleViewChange(option)}
                aria-pressed={view === option.value}
                disabled={option.disabled}
              >
                {option.label}
              </button>
            ))}
          </div>
          <div className="operational-calendar__nav">
            <button type="button" onClick={onToday} className="operational-calendar__nav-btn">Hoy</button>
            <div className="operational-calendar__arrows">
              <button type="button" onClick={onPrev} aria-label="Periodo anterior">
                <span aria-hidden="true">‹</span>
              </button>
              <button type="button" onClick={onNext} aria-label="Periodo siguiente">
                <span aria-hidden="true">›</span>
              </button>
            </div>
            {typeof onToggleFullscreen === 'function' ? (
              <button
                type="button"
                className={`operational-calendar__fullscreen${isFullscreen ? ' is-active' : ''}`}
                onClick={onToggleFullscreen}
                aria-label={isFullscreen ? 'Salir de pantalla completa' : 'Ver en pantalla completa'}
              >
                ⤢
              </button>
            ) : null}
          </div>
        </div>
      </div>

      <div className="operational-calendar__filters">
        <div className="operational-calendar__filters-meta">
          <span>
            {filters.filter((filter) => filter.isActive).length}/{filters.length} filtros activos · {visibleEventCount} rangos
          </span>
          {showReset ? (
            <button type="button" onClick={onFilterReset} className="operational-calendar__reset">Mostrar todos</button>
          ) : null}
        </div>
        <div className="operational-calendar__filter-options">
          {filters.map((filter) => (
            <button
              type="button"
              key={filter.key}
              className={`operational-calendar__filter${filter.isActive ? ' is-active' : ''}`}
              onClick={() => onFilterToggle(filter.key)}
            >
              <span className={`operational-calendar__filter-dot operational-calendar__filter-dot--${filter.tone}`} aria-hidden="true" />
              {filter.label}
            </button>
          ))}
        </div>
      </div>

      <div className="operational-calendar__grid" role="grid" aria-label="Calendario operativo">
        <div className="operational-calendar__weekdays">
          {weekdays.map((weekday) => (
            <div key={weekday} className="operational-calendar__weekday">
              {weekday}
            </div>
          ))}
        </div>
        <div className="operational-calendar__days">
          {days.map((day) => {
            const isSelected = selectedDayKey === day.key;
            const dayClasses = [
              'operational-calendar__day',
              day.isCurrentMonth ? '' : 'is-muted',
              day.isToday ? 'is-today' : '',
              isSelected ? 'is-selected' : '',
            ]
              .filter(Boolean)
              .join(' ');
            return (
              <button
                type="button"
                key={day.key}
                className={dayClasses}
                onClick={() => onDaySelect(day.key)}
                onContextMenu={(event) => {
                  event.preventDefault();
                  onDayContextMenu(event, day.key);
                }}
              >
                <div className="operational-calendar__day-header">
                  <span className="operational-calendar__date">{day.label}</span>
                </div>
                <div className="operational-calendar__day-body">
                  {day.pills.length ? (
                    day.pills.map((pill) => (
                      <span
                        key={`${day.key}-${pill.key}`}
                        className={`operational-calendar__pill operational-calendar__pill--${pill.tone}`}
                      >
                        {pill.label}
                      </span>
                    ))
                  ) : (
                    <span className="operational-calendar__empty">Sin operaciones</span>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

OperationalCalendar.propTypes = {
  title: PropTypes.string.isRequired,
  view: PropTypes.string.isRequired,
  viewOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      disabled: PropTypes.bool,
    })
  ).isRequired,
  onViewChange: PropTypes.func.isRequired,
  onPrev: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
  onToday: PropTypes.func.isRequired,
  weekdays: PropTypes.arrayOf(PropTypes.string).isRequired,
  days: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      isCurrentMonth: PropTypes.bool,
      isToday: PropTypes.bool,
      pills: PropTypes.arrayOf(
        PropTypes.shape({
          key: PropTypes.string.isRequired,
          label: PropTypes.string.isRequired,
          tone: PropTypes.string.isRequired,
        })
      ),
    })
  ).isRequired,
  selectedDayKey: PropTypes.string,
  onDaySelect: PropTypes.func.isRequired,
  onDayContextMenu: PropTypes.func.isRequired,
  filters: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      tone: PropTypes.string.isRequired,
      isActive: PropTypes.bool.isRequired,
    })
  ).isRequired,
  onFilterToggle: PropTypes.func.isRequired,
  onFilterReset: PropTypes.func.isRequired,
  showReset: PropTypes.bool,
  visibleEventCount: PropTypes.number,
  onToggleFullscreen: PropTypes.func,
  isFullscreen: PropTypes.bool,
};

OperationalCalendar.defaultProps = {
  selectedDayKey: '',
  showReset: false,
  visibleEventCount: 0,
  onToggleFullscreen: undefined,
  isFullscreen: false,
};

export default OperationalCalendar;
